import React, { useEffect, useState, useRef } from 'react';

import { ListFilterIcon, XIcon } from 'lucide-react';

import { isEqual } from 'lodash';

import { alpha, Box, ButtonGroup, Drawer, IconButton, Paper, Stack, Tooltip, Typography, Collapse, Zoom, useTheme } from '@mui/material';

import { Button, FilterSheetFooter, hideScrollbar, TOOLBAR_CONTROL_HEIGHT } from '@repo/ui';
import { useScanRouting } from 'contexts';
import { DateRangePickerFilter, LocationFilter, MultiSelectFilter, SearchFilter, SliderFilter, StatusChipFilter } from 'components';
// Direct file import, not the `components` barrel — this file is part of that barrel.
import { toolbarClearEdgeSx, toolbarClearSx, toolbarLabelSx, toolbarStartIconSx } from '../ActionLine/toolbarCollapse';
import { vhMinus } from 'utils/textZoom'

/**
 * "Open the drawer, scrolled to this field."
 *
 * ## Why a token rather than a plain `string | null`
 *
 * The request has to be REPEATABLE for the same field: close the drawer, click the same
 * chip again, and it must open again. A bare `field` prop cannot express that — the
 * value would not change, so no effect would fire — and the usual repair (parent clears
 * it on consume) needs a callback and a round trip through the parent's render, which is
 * a second piece of state to keep in step. A monotonically increasing token makes each
 * request distinct by construction, so the effect keys on the token and the field rides
 * along as data.
 *
 * ⚠️ Deliberately NOT a controlled `open` prop. This drawer's own button is still its
 * primary opener, and making `open` controlled would force every one of the ~30 pages
 * rendering `ActionLine` to own that state. This is additive: absent, nothing changes.
 */
export interface FilterOpenRequest {
  /** A column `field` — the same key the drawer's own `columns` are declared with. */
  field: string
  /** Bump on every request. Only a CHANGE is acted on; the initial value is ignored. */
  token: number
}

interface FiltersProps {
  apiRef?: any
  disabled?: boolean,
  filters?: any[],
  /** The applied filter set, reported to the parent so it can drive server-side filtering. */
  onChange?: (filters: any[]) => void,
  columns?: [object],
  rows?: [object]
  /** Page-specific extra controls rendered at the top of the filters drawer. */
  extraFilters?: React.ReactNode
  /**
   * Where `extraFilters` sits relative to the column filters. Defaults to `'after'`, which
   * is what every existing consumer gets and is the right default: a page's extras are
   * usually incidental (`Show archived`), so they belong at the end and the column filters
   * keep table-column order.
   *
   * `'before'` exists because an extra is not always incidental. The analytics drawer's
   * DATE RANGE is the case: it scopes every number on the page, and it is an extra only for
   * a mechanical reason — routing an always-applied facet through `columns` would count it
   * in `internalFilters.length` and pin the Filter badge at a permanent non-zero minimum.
   * Being forced into `extraFilters` should not also force it to the BOTTOM of the drawer,
   * away from the order the toolbar presents.
   */
  extraFiltersPosition?: 'before' | 'after'
  /** Commit page-specific extra filters. Fires on the same debounce as the column filters. */
  onApply?: () => void
  /**
   * True when the extra filters have uncommitted changes.
   *
   * Formerly the gate on the Apply button; now a TRIGGER. There is no Apply, so a page's
   * draft→applied handoff has to be started by something, and this flipping to `true` is it —
   * the debounce fires, `onApply` commits the draft, and the page's own state flips it back.
   */
  extraFiltersDirty?: boolean
  /**
   * Reset the page-specific extra filters.
   *
   * The extras are an opaque `ReactNode` whose state the PAGE owns, so Clear cannot reach it
   * from in here — without this callback a control like "Show archived" stays on after Clear
   * and the button quietly under-delivers on its label. Pages that render `extraFilters`
   * should pass this and reset every piece of that state (draft AND applied) to its default.
   */
  onClearExtraFilters?: () => void
  /**
   * Open the drawer at a named field, from OUTSIDE it — today the Analytics page's
   * applied-filter chips, each of which reopens the dimension it stands for.
   *
   * See `FilterOpenRequest` for why it carries a token.
   */
  openRequest?: FilterOpenRequest | null
  /**
   * Which staged filters the applied-count BADGE counts. Defaults to all of them.
   *
   * ## Why this exists, and why it is opt-in
   *
   * The badge sits on a CLOSED panel, so it is read as a claim about what that panel is
   * hiding. For the ~30 pages whose filters live only in this drawer that claim and
   * `internalFilters.length` are the same number, which is why the default counts
   * everything and nothing about those pages changes.
   *
   * The Analytics page is the one that promotes drawer fields into first-class TOOLBAR
   * controls (Certificate status, Scan status), and there the two come apart: with both
   * controls on screen and both applied, the badge read "2" while the two chips sat an
   * inch to its left — asserting two hidden filters where there were none. It passes a
   * predicate that excludes a field while that field's own toolbar control is visible.
   *
   * ⚠️ SCOPE: this narrows the BADGE ONLY. The clear-all `X`, the button-group divider and
   * everything the drawer renders still key off the full `internalFilters`, because those
   * answer a different question — "are any filters applied at all" — for which the honest
   * answer is unchanged. So a page can legitimately show the clear-`X` with no badge: three
   * filters applied, all three visible in the toolbar, none behind the button.
   */
  countFilter?: (filter: any) => boolean
}

/**
 * How long the drawer waits after the last change before committing the staged filters.
 *
 * ONE timer for the WHOLE drawer, not one per field: an operator setting Product AND Owner AND
 * Status should get a single query once they stop, not three that re-sort the list under their
 * hand as they go. Every field stages into the same `internalFilters` array, so every change
 * restarts this one timer.
 *
 * Long enough that a multi-tag scan run (or a burst of checkbox clicks) coalesces into one
 * query, short enough that a single change still reads as immediate.
 */
const AUTO_APPLY_MS = 400

export const Filters = ({
  apiRef,
  disabled = false,
  filters = [],
  onChange = () => {},
  columns,
  rows,
  extraFilters,
  extraFiltersPosition = 'after',
  onApply,
  extraFiltersDirty = false,
  onClearExtraFilters,
  openRequest = null,
  countFilter,
}: FiltersProps) => {

  const theme = useTheme()
  // VRG-265 — Clear has to clear the SCANS too. A scanned entity is held above these fields (so
  // it survives this drawer unmounting), and a field folds it back into its value on mount; wipe
  // only the staged filters and the scans re-stage themselves, so Clear appears to do nothing.
  //
  // `target` / `releaseTarget` scope a filter field's scan target to this drawer being OPEN —
  // see `handleClose`.
  const { clearAllScanned, target, releaseTarget } = useScanRouting()
  const [open, setOpen] = useState(false);
  const [internalFilters, setInternalFilters] = useState<any[]>(filters);
  /**
   * One entry per rendered filter field, indexed by its position in `filterableColumns`.
   *
   * TYPED, where it used to be a bare `useRef([])`. That inferred `never[]`, so BOTH of
   * its uses were type errors sitting in the platform's baseline — the write
   * (`HTMLDivElement | null` into `never`) and the read (`.scrollIntoView` on `never`).
   * The annotation is the whole fix; nothing about the runtime behaviour changes.
   */
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  /**
   * An external open request that arrived while the drawer was CLOSED, waiting for the
   * slide to finish. The fields do not exist in the DOM until then (`Drawer` unmounts
   * its children), so a `scrollIntoView` fired at request time would find nothing.
   */
  const [pendingField, setPendingField] = useState<string | null>(null)

  /**
   * What the applied-count BADGE reports — see `countFilter` for why this is not always
   * `internalFilters.length`.
   *
   * Without a `countFilter` the two are identical by construction, so every page that does
   * not opt in is byte-for-byte unchanged. Deliberately NOT reused for the clear-all `X` or
   * the button-group divider below: those ask "are any filters applied", which the full
   * array answers and this does not.
   */
  const badgeCount = countFilter ? internalFilters.filter(countFilter).length : internalFilters.length

  /**
   * The last `openRequest` token acted on. Seeded from the MOUNT-TIME value so a parent
   * that already holds a request when this mounts does not have it replayed — only a
   * CHANGE counts.
   */
  const lastOpenTokenRef = useRef<number | null>(openRequest?.token ?? null)

  /**
   * The last `filters` PROP value this component reacted to.
   *
   * Not "the last thing I committed" — the distinction is the whole reason the sync effect
   * below is safe. Many parents pass a fresh array literal every render (this component's own
   * `filters = []` default does it too), so the prop's IDENTITY churns constantly while its
   * CONTENT never moves. Comparing against the last-seen prop by content is what tells an
   * actual parent-side change apart from that churn.
   */
  const lastSeenFilters = useRef<any[]>(filters)

  /**
   * The filter set the parent and this drawer already AGREE on — updated on commit, and on
   * adopting a parent change. The debounce below refuses to re-commit a set matching it.
   */
  const lastCommittedFilters = useRef<any[]>(filters)

  /**
   * ADOPT a parent-side change to `filters`.
   *
   * `useState(filters)` reads the prop once, at mount, and never again — so when a parent
   * changed the applied set afterwards, the staged copy in here did not follow. The visible
   * failure was "Clear filters": the page reset its own state to `[]`, the list unfiltered
   * itself, and the drawer went on showing the old selections with the Filter button's count
   * badge still lit — the control claiming a filter the list was no longer applying. This was
   * previously worked around at the call site by remounting the toolbar with a changing `key`,
   * which is a real fix's understudy: it throws away the whole toolbar (search box, sort menu,
   * scroll position) to move one piece of state.
   *
   * The naive repair — sync on every `filters` change — is worse than the bug, twice over, and
   * both hazards are why this is written the long way:
   *
   *  1. COMMIT LOOP. The effect below debounce-commits `internalFilters` back to the parent,
   *     which hands it straight back as a new `filters` prop (`Scans` and both support lists
   *     are exactly this shape). Adopting that echo would set state, which re-arms the
   *     debounce, which commits again. The SECOND guard stops it: an echo is by definition
   *     content-equal to what is already staged, so there is nothing to adopt.
   *  2. CLOBBERING IN-FLIGHT EDITS. On a parent that never echoes (every `Table` page — the
   *     drawer's output goes to `onFiltersChange` while `filters` stays whatever the page
   *     passed, often a fresh literal), syncing on identity would re-apply that unchanged prop
   *     on every render and wipe selections out from under someone staging them in the open
   *     drawer. The FIRST guard stops that one: same content as last seen, so not a change.
   *
   * `internalFilters` is read without being a dependency, deliberately. The effect body runs
   * synchronously during the commit phase of the render that changed `filters`, so the closure
   * it reads is that render's — current, not stale. Adding it to the deps would re-run this on
   * every staged tick and defeat guard 1.
   */
  useEffect(() => {
    if (isEqual(filters, lastSeenFilters.current)) return
    lastSeenFilters.current = filters
    // Already staged (a parent that normalises what we commit can echo a content-equal set
    // through a different shape); adopting it would only churn identity and re-arm the debounce.
    if (isEqual(filters, internalFilters)) return
    // The parent is authoritative about what is APPLIED, so an adopted set is by definition
    // already committed — recording it here keeps the debounce from sending it straight back.
    lastCommittedFilters.current = filters
    setInternalFilters(filters)
    // `filters` ONLY — see the note above on why `internalFilters` is read but not depended on.
  }, [filters])


  const handleAddInternalFilter = (filter) => {
    // A filter is keyed by (field, operator) — so a range slider's '>=' and '<=' are two
    // distinct entries on the same field, and re-emitting one replaces it in place.
    // Empty value (cleared multiSelect, or a slider thumb back at min/max) removes it.
    const isEmpty =
      filter.value == null ||
      (Array.isArray(filter.value) && filter.value.length === 0)
    setInternalFilters(prev => {
      const rest = prev.filter(
        f => !(f.field === filter.field && f.operator === filter.operator)
      )
      const next = isEmpty
        ? rest
        : [...rest, { field: filter.field, operator: filter.operator, value: filter.value }]
      // Re-index ids so they stay unique/sequential.
      return next.map((f, i) => ({ ...f, id: i }))
    })
  }
  

  const handleClearInternalFilters = () => {
    clearAllScanned()
    setInternalFilters([])
    onClearExtraFilters?.()
  }

  /**
   * Report the applied set to the parent AND set the grid's own model.
   *
   * These were an `if (apiRef) … else onChange(…)`, which read as a choice between two ways of
   * filtering but is not one: the grid model is how the DataGrid narrows the rows it already
   * holds, while `onChange` is how a page learns what to ask the SERVER for. They are different
   * layers, and every list here uses a grid, so `apiRef` was always set and `onChange` was
   * therefore never called. The effect was that `onFiltersChange` — documented on `Table` as
   * "the filters drawer reports applied filters to the parent so it can drive SERVER-side
   * filtering" — never fired on any page that passed it (Certificates, Scans, Leads, Tag shop):
   * Apply narrowed only the current page of rows in the browser, and the status / product /
   * owner query params those pages compute were dead code. Verified in the browser: applying
   * Status produced a request with no `statuses` param at all.
   *
   * Calling both is what the two layers were always meant to do. A page that filters
   * server-side gets its query; the grid keeps narrowing what it holds, which is a no-op once
   * the server has already narrowed it (provided the column's value matches what the filter
   * carries — see the `valueGetter`s on the Certificates columns).
   */
  const handleRemoveAllFilters = () => {
    clearAllScanned()
    setInternalFilters([]);
    // Same reach as the drawer's Clear. The page-owned extras (e.g. Show archived) are not in
    // `internalFilters`, so without this the toolbar's clear-all and the drawer's Clear would
    // empty different amounts — the toggle surviving one button but not the other.
    onClearExtraFilters?.()
    // Commits IMMEDIATELY rather than through the debounce (clearing everything is a decision,
    // not a tick), so record it as committed — otherwise the debounce this state change arms
    // would send an identical `onChange([])` 400ms behind this one.
    lastCommittedFilters.current = []
    onChange([])
    if (apiRef) {
      apiRef.current.setFilterModel({items: []})
    }
  }

  /**
   * Commit the staged copy to BOTH layers, WITHOUT closing the drawer.
   *
   * Never closes, because the operator is normally still in the drawer (often still inside an
   * open dropdown) when a change applies.
   */
  const commitFilters = (next: any[]) => {
    lastCommittedFilters.current = next
    onChange(next)
    if (apiRef) {
      apiRef.current.setFilterModel({items: next})
    }
    // Commit page-specific extra filters (e.g. Show archived) on the same beat.
    onApply?.()
  }

  /**
   * AUTO-APPLY — every staged change commits itself. There is no Apply button.
   *
   * The drawer used to run TWO commit models side by side: a scan applied on its own (debounced),
   * while a checkbox click waited for Apply. That is one surface teaching two different rules
   * about what a click does, and after a scan the button invited a press that would change
   * nothing. One model instead — the ticks ARE the commit.
   *
   * It commits `internalFilters` — the STAGED array — and nothing else, which is the whole point.
   * Every field, and a scan, stage through `handleAddInternalFilter`, so the staged copy, the
   * toolbar count badge and each field's own badge already agree with each other; committing that
   * same array is what keeps them agreeing with the narrowed grid too. The VRG-265 trap (recorded
   * on `Shop.tsx`) was the opposite shape — a filter pushed into the grid WITHOUT passing through
   * the staged copy, so the list narrowed while the badge kept reading zero.
   *
   * DEBOUNCED at the DRAWER, not the field, so setting three facets costs one query rather than
   * three (see `AUTO_APPLY_MS`). `extraFiltersDirty` is a dependency because a page's extra
   * controls are drafts too, and flipping it true is the only signal this component gets that one
   * changed.
   *
   * The mount run is skipped deliberately: `internalFilters` is seeded FROM what is already
   * applied, so committing it would fire a redundant query (and `onApply`) on every open.
   */
  const mounted = useRef(false)
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    const timer = setTimeout(() => {
      // Nothing to say. The staged set already matches what the parent holds — which is what a
      // set ADOPTED from the parent looks like (see the sync effect above), and sending it back
      // would be a second, identical query for a change the parent made itself. `Clear filters`
      // is the case that fires: it resets the page, the drawer adopts `[]`, and without this the
      // adoption would bounce an `onChange([])` back 400ms later.
      //
      // `extraFiltersDirty` still forces a commit: the page-owned extras are not in
      // `internalFilters`, so an unchanged staged array is not evidence that nothing changed.
      if (!extraFiltersDirty && isEqual(internalFilters, lastCommittedFilters.current)) return
      commitFilters(internalFilters)
    }, AUTO_APPLY_MS)
    return () => clearTimeout(timer)
  }, [internalFilters, extraFiltersDirty])

  /**
   * A FILTER FIELD'S SCAN TARGET IS SCOPED TO THIS DRAWER BEING OPEN (2026-08-15).
   *
   * The target selector is a per-field control (`ScanTargetButton`) that lives INSIDE this
   * drawer, so an armed filter target used to survive with nothing on screen saying where taps
   * were landing. That was first annotated — a brand dot on the Filter button — and is now
   * removed instead: the toolbar's own search box has a scan control beside it and already
   * serves "tap a tag, find that record" with no drawer at all, so a filter target armed behind
   * a closed drawer has no job that is not already covered. What it does buy is the
   * invisible-scan failure class this area spent a long session eliminating. Every other filter
   * already requires this drawer to be open; the scan target now matches them.
   *
   * RELEASE, not end. The session stays alive and idle — ending it would force a QR re-pair for
   * the price of closing a panel, and an operator who learns that stops closing the panel, which
   * puts us back where we started. And it does NOT fall back to the toolbar search: a navigation
   * must never silently repoint where physical taps land (see `releaseTarget`).
   */
  const handleClose = () => {
    setOpen(false)
    // A request that never got to scroll (the drawer was closed before the slide
    // finished) must not fire against the NEXT open, which would be a scroll nobody
    // asked for.
    setPendingField(null)
    if (target?.kind === 'filter-field') releaseTarget()
  }

  const handleFilterScroll = (index) => {
    itemRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  // only allow filters for appropriate columns
  const filterableColumns = columns?.filter(column => column.headerName != false && column.filterable != false).map((column) => {
    if (column.type == undefined) {
      console.error(`Column type is undefined for ${column.headerName}. Filters need column type to render. Column was skipped`)
      return
    } else {
      return column
    }
  }).filter(column => column !== undefined)

  /**
   * Bring a named field into view. Resolved against `filterableColumns` — the SAME list
   * the fields are rendered from — so an index can never point at a different field than
   * the one that was asked for. A field that is not filterable simply does not scroll.
   *
   * ## `behavior: 'auto'`, NOT the `smooth` that `handleFilterScroll` uses
   *
   * Two different gestures. `handleFilterScroll` nudges the list while the user is
   * already inside the drawer looking at it, where a glide explains what moved. This
   * fires immediately after the drawer has SLID IN, so a smooth scroll would be a second
   * motion chasing the first — the same "slides in, then jumps" defect `AnalyticsPage`'s
   * Customize rail documents. The user asked for a field and should arrive at it.
   *
   * ⚠️ It is also the only version that is verifiable. Smooth scrolling is driven by the
   * compositor, and the Claude preview pane's animation clock is frozen (rAF fires zero
   * times) — measured on this very element: `scrollIntoView({behavior:'smooth'})` left
   * `scrollTop` at 0 while `'auto'` moved it to 226 in the same call.
   */
  const scrollToField = (field: string) => {
    // `any`, matching every other read of a column in this file. `FiltersProps.columns`
    // is declared `[object]` — a one-element tuple of the empty object type — so EVERY
    // property access on a column here is already a type error in the platform's
    // baseline (`headerName`, `type`, `field`, `searchParams`, all of them). Widening
    // that prop to a real column type is the right fix and it is not this change's:
    // it is a shared component rendered by ~30 pages. Casting here keeps this addition
    // from adding a thirtieth error to that pile.
    const index = filterableColumns?.findIndex((column: any) => column.field === field) ?? -1
    if (index === -1) return
    itemRefs.current[index]?.scrollIntoView({ behavior: 'auto', block: 'start' })
  }

  useEffect(() => {
    if (!openRequest) return
    if (lastOpenTokenRef.current === openRequest.token) return
    lastOpenTokenRef.current = openRequest.token
    if (open) {
      // Already open — the fields are mounted, so this is just a scroll. That is the
      // case where a second chip is clicked without closing the drawer first.
      scrollToField(openRequest.field)
      return
    }
    setPendingField(openRequest.field)
    setOpen(true)
    // The TOKEN is the whole trigger; `field` rides along as data and re-running on it
    // would fire twice for one request. See `FilterOpenRequest`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest?.token])

  return (
    <>
      <ButtonGroup 
      variant="contained"
      disableElevation
      color="secondary"
      sx={{
        // `& > button:first-of-type`, structurally — the same fix Sort needed. This read
        // `.MuiButtonGroup-firstButton`, which matches NOTHING in this MUI version (children carry
        // `MuiButtonGroup-grouped`), so the divider it declared had never rendered; what was visible
        // was MUI's own default border in the default-palette purple, until that was neutralised
        // across the toolbar row.
        //
        // Drawn as an inset shadow, not a border: ActionLine neutralises ButtonGroup border colours
        // for the whole row from a three-class selector, which out-specifies anything written here.
        // A shadow is a different property, so the two rules stop competing.
        /*
          🚨 THE INSET-SHADOW DIVIDER IS GONE, because the premise it was built on is false.

          It existed on the reasoning quoted above — that `ActionLine` neutralises ButtonGroup
          border colours for the whole row, so a border here could never win and a shadow was
          needed instead. MEASURED on the live page, that is not what happens: this button
          computes `border-left: 1px rgb(231,231,231)` and `border-right: 1px rgb(231,231,231)`.
          The borders are alive and visible.

          So the shadow was not replacing a dead border, it was drawing a SECOND line beside a
          live one — and the X on the other side contributes its own left border. That is the
          doubled edge: Filter's right border + the inset shadow + the X's left border, three
          declarations for one divider.

          The border alone is the divider now. The two facing borders are collapsed into one by
          pulling the X back over Filter's edge (below) — the same thing MUI's own ButtonGroup
          does for direct children, which it cannot do here because the X is wrapped in a
          `<Collapse>` and so is not a direct child for `:not(:first-of-type)` to match.
        */
        // Squared right corners ONLY while the X is actually beside it — which is a QUERY,
        // not the applied flag, because the X collapses out of the row before any label does
        // (VRG-535 second pass). Squaring on `applied` alone would leave a flat right edge
        // with nothing attached to it at every width below the clear rung.
        '& > button:first-of-type': {
          borderTopRightRadius: 8,
          borderBottomRightRadius: 8,
          ...(internalFilters.length > 0 ? toolbarClearEdgeSx : {}),
        },
        // The Collapse is the X's wrapper and Filter's actual sibling, so the overlap belongs
        // here rather than on the button — a negative margin on the button would be measured
        // inside a wrapper that has already reserved the full width, and the gap would stay.
        // That is also why the collapse rung lands HERE and not on the button: hiding the
        // button leaves this wrapper holding its 48px.
        '& > .MuiCollapse-root': {
          ...toolbarClearSx(),
          marginLeft: '-1px',
          '& button': {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          },
        },
      }}
      >
        <Tooltip title={badgeCount > 0 ? `Filter — ${badgeCount} applied` : 'Filter'} enterDelay={600}>
        <Button
        onClick={() => setOpen(true)}
        disableTouchRipple
        variant="secondary"
        size="large"
        disabled={disabled}
        /*
          The accessible name. The visible "Filter" span is `display: none` once the toolbar
          container narrows (VRG-535), which removes it from the accessibility tree as well
          as the layout, so without this the collapsed button announces as unnamed.

          It carries the applied COUNT because the count badge is a `<Typography>` inside the
          button, so the pre-collapse accessible name was the bare concatenation "Filter3" —
          worse than either half. Naming it explicitly fixes that at every width, not just
          the collapsed one.
        */
        aria-label={badgeCount > 0 ? `Filter, ${badgeCount} applied` : 'Filter'}
        startIcon={<ListFilterIcon color={theme.palette.primary[800]} strokeWidth={1}/>}
        sx={{
          // Zeroes `startIcon`'s margins while the label is hidden so the glyph centres.
          // Same `applied` value as the label below, or the two disagree and the icon sits
          // 12px right of centre on an applied, unlabelled button.
          ...toolbarStartIconSx('filter', badgeCount > 0),
          // The two `border` declarations that were here used SINGLE quotes around a template
          // expression -- `'1px solid ${theme.palette.grey[300]}'` -- so `${...}` was never
          // interpolated and the browser dropped the invalid shorthand. Filter therefore
          // computed to `0px` in MUI's default purple while its neighbours drew grey[200]
          // borders, which is what made it the one button in the row with no outline. The row is
          // borderless now (see ActionLine), so they are simply gone rather than repaired.
          alignItems: 'center',
          display: 'flex',
          fontSize: 16,
          justifyContent: 'center',
          '&:hover': {
            backgroundColor: theme.palette.primary[100],
          }
        }}>
          {/* NO scan indicator here (removed 2026-08-15). It reported a state that can no longer
              occur: a filter scan target cannot outlive this drawer being open (`handleClose`),
              so there is nothing left for a dot on the closed drawer's own button to say. Kept
              "as belt-and-braces" it would be a second affordance for an impossible state, which
              is worse than either one alone. The applied-count badge below is a DIFFERENT signal
              ("N filters applied") and stays. */}
          {/* Label collapses to icon-only as the TOOLBAR CONTAINER narrows — third of five
              to go, after Download and Views (VRG-535, `toolbarCollapse.ts`). It was a
              VIEWPORT query at `md`, which never fired at the width the row wraps at: at a
              1280 viewport with the sidebar expanded the container is 943px and the row was
              two rows tall while `md` (900px of VIEWPORT) was comfortably satisfied.

              Filter and Sort sit third and fourth rather than first because the badge below
              survives the collapse — "some filtering is applied" is still legible with the
              word gone, which is not true of Download or Views.

              ⚠️ AND ONCE THE BADGE IS ACTUALLY THERE, the word goes at EVERY width, not just
              below the rung (`badgeCount > 0`). That is the same judgement one step earlier:
              the badge has taken the label's place, so keeping both makes this the widest
              control in the row precisely when the row can least afford it. The tooltip and
              `aria-label` above both still read "Filter — N applied". See the applied-state
              section of `toolbarCollapse.ts` for the measurements that made this necessary. */}
          <Box component="span" sx={toolbarLabelSx('filter', 'inline', badgeCount > 0)}>Filter</Box>
          {/* `badgeCount`, not `internalFilters.length` — see `countFilter`. Identical for
              every page that does not pass one. The `in` gate moves with it, or a page
              whose applied filters are all visible elsewhere would render a "0". */}
          <Zoom in={badgeCount > 0 && !disabled} timeout={200} mountOnEnter unmountOnExit>
            <Stack 
            sx={{
              width: 22,
              height: 22,
              borderRadius: 45,
              margin: 1,
              marginLeft: 3,
              background: 'linear-gradient(90deg, rgba(130,90,195,1) 0%, rgb(55, 39, 137) 100%)',
              fontSize: 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Typography color="common.white" sx={{fontSize: 12, fontWeight: 400}}>{badgeCount}</Typography>
            </Stack>
          </Zoom>
        </Button>
        </Tooltip>
        <Collapse in={internalFilters.length > 0} orientation="horizontal" timeout={500} mountOnEnter unmountOnExit>
          <Button 
          onClick={handleRemoveAllFilters}
          disableTouchRipple
          variant="secondary"
          disabled={disabled}
          size="large"
          sx={{ 
            minHeight: TOOLBAR_CONTROL_HEIGHT,
            minWidth: 48,
            padding: 1,
            height: TOOLBAR_CONTROL_HEIGHT,
            width: 48,
            // 20px, matching Sort's clear-X. `flexShrink: 0` because the glyph is inside a flex
            // button and was being squeezed narrower than its declared size.
            '& svg': { width: 20, height: 20, flexShrink: 0 },
            '&:hover': {
              // action.hover, not primary[100] — that is the DARK-mode surface token and in light
              // it is darker than the surface it sits on.
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.error.main
            }
          }}>
            <XIcon strokeWidth={1}/>
          </Button>
        </Collapse>
      </ButtonGroup>
      <Drawer
      anchor="right"
      open={open}
      // The SAME close path as the header's X (and not `setOpen(false)`), so a backdrop click or
      // Escape releases the scan target too. Two close paths that did different things is how an
      // armed target would survive the drawer.
      onClose={handleClose}
      /**
       * The other half of `openRequest`. The drawer unmounts its children, so the field
       * rows exist only once the slide has finished — this is the first moment
       * `scrollIntoView` has anything to find. Cleared on use so it fires once per
       * request, and cleared again in `handleClose` for a request that never got here.
       */
      SlideProps={{
        onEntered: () => {
          if (!pendingField) return
          scrollToField(pendingField)
          setPendingField(null)
        },
      }}
      // The filter fields render an autocomplete dropdown in a portaled Popper (to escape
      // this drawer's overflow:auto clipping). Don't let the drawer's modal focus-trap
      // yank focus back from that dropdown / the field being typed into.
      disableEnforceFocus
      sx={{
        margin: 4, 
        borderRadius: 3,
        '& .MuiDrawer-paper': {
          maxHeight: vhMinus(32),
          margin: 4,
          borderRadius: 3,
        }
      }}>
        <Paper
        sx={theme => ({
          borderRadius: 3,
          minWidth: 500,
          maxWidth: 500,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
          overflow: 'auto',
          overflowX: 'hidden',
          // Flat surface (kill MUI's dark-mode elevation overlay) so the body, the
          // sticky header, and the absolute footer all read as one uniform
          // background.paper instead of three subtly different shades.
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
          ...hideScrollbar,
        })}
        >
          <Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={theme => ({
                position: 'sticky',
                top: 0,
                minWidth: 500,
                // Match the drawer's surface (Paper uses background.paper) so the
                // sticky header reads as the same surface instead of a lighter bar
                // in dark mode. Opaque (not transparent) so it masks scroll.
                background: theme.palette.background.paper,
                p: 6,
                pb: 6,
                zIndex: 1
              })}>
              <Typography variant="body1">Filters</Typography>
              <IconButton 
                color="error"
                onClick={handleClose} 
                sx={{
                  color: theme.palette.grey[900],
                  '&:hover': {
                  backgroundColor: alpha(theme.palette.red[500],.05),
                  color: theme.palette.error.main
                  }            
                }}>
                <XIcon strokeWidth={1} />
              </IconButton>
            </Stack>
            <Stack
              p={6}
              pt={0}
              spacing={6}
              sx={{
                maxHeight: vhMinus(172),
                minHeight: vhMinus(172),
                overflowY: 'auto',
                overflowX: 'hidden',
                // VERA-914: a filterable column whose filter renders nothing (e.g. a
                // number slider where min==max, or an unhandled type) still produces an
                // empty Stack child, which the `spacing={6}` margin then treats as a real
                // row — injecting inconsistent extra gaps between the visible filters.
                // Collapse those empty sections so gaps stay uniform.
                '& > .MuiStack-root:empty': { display: 'none' },
                ...hideScrollbar,
              }}
            >
              {/* Extras FIRST when the page asks for it — see `extraFiltersPosition`. */}
              {extraFiltersPosition === 'before' && extraFilters}
              {/* iterate over fitlreableColumns */}
              {filterableColumns.map((column, index) => {
                // get existing filters to for the column to pass down to filter
                const columnFilters = internalFilters.filter(filter => filter.field == column.field)
                // render filter based on column type
                return (
                  <React.Fragment key={`filter-${index}`}>
                  <Stack
                    ref={(el) => (itemRefs.current[index] = el)}
                  >
                    {column.type == 'string' && (
                      <SearchFilter 
                        column={column} 
                        rows={rows}
                        filter={columnFilters.length > 0 ? columnFilters[0].value : []}
                        searchParams={column.searchParams}
                        onSearch={() => handleFilterScroll(index)}
                        onChange={handleAddInternalFilter}
                      />
                    )}
                    {column.type == 'multiSelect' && (
                      <MultiSelectFilter
                        column={column}
                        rows={rows}
                        filter={columnFilters.length > 0 ? columnFilters[0].value : []}
                        onChange={handleAddInternalFilter}
                      />
                    )}
                    {column.type == 'statusChip' && (
                      <StatusChipFilter
                        column={column}
                        filter={columnFilters.length > 0 ? columnFilters[0].value : []}
                        onChange={handleAddInternalFilter}
                      />
                    )}
                    {column.type == 'location' && (
                      <LocationFilter
                        column={column}
                        filter={columnFilters.length > 0 ? columnFilters[0].value : []}
                        onChange={handleAddInternalFilter}
                      />
                    )}
                    { column.type == 'number' && (
                      <SliderFilter 
                        column={column} 
                        rows={rows}
                        // TODO: Tre, this one passes two filters, we could pass it just values instead here to keep it consistent with the rest?
                        filters={[columnFilters]}
                        onChange={handleAddInternalFilter}
                      />
                    )}
                    { column.type == 'dateTime' && (
                      <DateRangePickerFilter 
                        column={column} 
                        rows={rows}
                        filters={columnFilters}
                        onChange={handleAddInternalFilter}
                      />
                    )}
                     { column.type == 'boolean' && (
                      <div/>
                      // TODO: make radio select filter for boolean
                    )}
                  </Stack>
                  </React.Fragment>
                )
              })}
              {/* Page-specific extra controls (e.g. Show archived) render AFTER the column
                  filters by default, so the column filters stay in table-column order and the
                  non-column extras sit at the end. (Also renders even with no filterable
                  columns, unlike the old index===0 placement.) A page that needs an extra
                  FIRST opts in via `extraFiltersPosition='before'`; this stays the default. */}
              {extraFiltersPosition !== 'before' && extraFilters}
            </Stack>
            {/* CLEAR ONLY — the drawer auto-applies, so there is no Apply here either (the
                dropdowns lost theirs for the same reason). Clear is an ACTION, not a commit, so
                it survives the change; it also matters more now, because auto-apply leaves no
                undo and this is the only way back to an unfiltered list from inside the drawer.
                There is no Cancel to remove: this footer never had one, and under auto-apply the
                word would be a lie rather than a redundancy — nothing is pending to cancel.

                SHARED with `apps/app`'s filter sheets as of VERA-315 — the same `@repo/ui`
                component renders both, so the two apps cannot drift on what Reset/Clear means
                (it clears the STAGED set and commits nothing itself — true under this drawer's
                debounce and under the app's explicit Apply alike). Omitting `onApply` IS how
                the auto-apply mode is selected: no Apply button is rendered. `mode='panel'`
                gives the right-aligned 40px control this footer already had.

                Positioning stays here, not in the shared component: `absolute` + the 500px
                width lock + the opaque `background.paper` are this drawer's geometry, and the
                24px gutter matches THIS panel's own 24px header. `apps/app`'s side panel pays
                16px for the same reason against its own header. */}
            <FilterSheetFooter
              mode="panel"
              resetLabel="Clear"
              onReset={handleClearInternalFilters}
              sx={{
                position: 'absolute',
                bottom: 0,
                // The shared default is `marginTop: auto` + a 24px top gap, which is what a
                // flex-column sheet needs. This footer is absolutely positioned instead, so
                // both are neutralised and replaced by the drawer's own 16/24 padding.
                marginTop: 0,
                paddingTop: '16px',
                paddingBottom: '16px',
                paddingLeft: '24px',
                paddingRight: '24px',
                minWidth: 500,
                maxWidth: 500,
                zIndex: 1,
                // Same surface as the body (and header) so the panel reads as one
                // uniform background — not a lighter footer bar. Opaque to mask scroll.
                background: theme.palette.background.paper,
              }}
            />
          </Stack>
        </Paper>
      </Drawer>
    </>
  );
}
